import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./UserDetailsCard.css";
import { User } from "../models/user";
import { useTranslation } from "../i18n/UseTranslation";

type Props = { user: User };

function formatDate(iso: string | null, locale: string) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });
}

function maskIban(iban: string | null) {
  if (!iban) return "—";
  return iban.replace(/(.{4})/g, "$1 ").trim();
}

function UserDetailsCard({ user }: Props) {
  const { t, language } = useTranslation();
  const translationUser = t.users;
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const localeMap: Record<string, string> = { fr: "fr-FR", en: "en-US", es: "es-ES" };
  const currentLocale = localeMap[language] || "fr-FR";

  const fullName = useMemo(
    () => `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim() || t.users.unnamed,
    [user.first_name, user.last_name, t]
  );

  const initials = useMemo(() => {
    const first = user.first_name?.[0] ?? "";
    const last  = user.last_name?.[0]  ?? "";
    return (first + last).toUpperCase() || user.email?.[0]?.toUpperCase() || "?";
  }, [user.first_name, user.last_name, user.email]);

  const roleName = (user.role as any)?.name ?? (typeof user.role === "string" ? user.role : "—");

  const handleCopy = () => {
    navigator.clipboard.writeText(user.firebaseUid ?? "");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="udc-card">

      <div className="udc-header">
        <div className="udc-header-left">
          {user.profile_picture ? (
            <img className="udc-avatar udc-avatar--img" src={user.profile_picture} alt={fullName} />
          ) : (
            <div className="udc-avatar udc-avatar--initials">{initials}</div>
          )}
          <div>
            <p className="udc-name">{fullName}</p>
            <p className="udc-email">{user.email}</p>
          </div>
        </div>
        <div className="udc-header-right">
          {roleName !== "—" && (
            <span className="udc-role-badge">{translationUser[roleName as keyof typeof translationUser] || roleName}</span>
          )}
          <button className="patch-user-save" onClick={() => navigate(`/users/${user.firebaseUid}/edit`)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
            {t.users.edit}
          </button>
        </div>
      </div>

      <div className="udc-grid">

        <div className="udc-field udc-field--full udc-field--uid">
          <div className="udc-field-inner">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="udc-field-icon"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2a7.2 7.2 0 0 1-6-3.22c.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08a7.2 7.2 0 0 1-6 3.22z"/></svg>
            <div className="udc-field-content">
              <p className="udc-field-label">{t.users.uid}</p>
              <p className="udc-field-value udc-field-value--mono">{user.firebaseUid ?? "—"}</p>
            </div>
          </div>
          <button className="udc-copy-btn" onClick={handleCopy} title={t.users.copyUid} aria-label={t.users.copyUid}>
            {copied ? (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="udc-copy-check"><polyline points="20 6 9 17 4 12"/></svg>
            ) : (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
            )}
          </button>
        </div>

        <div className="udc-field">
          <p className="udc-field-label">{t.users.phone}</p>
          <p className="udc-field-value">{user.phone_number ?? "—"}</p>
        </div>

        <div className="udc-field">
          <p className="udc-field-label">{t.users.company}</p>
          <p className="udc-field-value">{user.company?.name ?? "—"}</p>
        </div>

        <div className="udc-field">
          <p className="udc-field-label">{t.users.role}</p>
          <p className="udc-field-value">{translationUser[roleName as keyof typeof translationUser] || roleName}</p>
        </div>

        <div className="udc-field">
          <p className="udc-field-label">{t.users.iban}</p>
          <p className="udc-field-value udc-field-value--mono udc-field-value--sm">{maskIban(user.iban ?? null)}</p>
        </div>

        <div className="udc-field">
          <p className="udc-field-label">{t.users.gender}</p>
          <p className="udc-field-value">{user.gender ? t.users[user.gender as keyof typeof t.users] || user.gender : "—"}</p>
        </div>

        <div className="udc-field">
          <p className="udc-field-label">{t.users.birthDate}</p>
          <p className="udc-field-value">{formatDate(user.birth_date ?? null, currentLocale)}</p>
        </div>

        <div className="udc-field udc-field--full">
          <p className="udc-field-label">{t.users.address}</p>
          <p className="udc-field-value">{user.address ?? "—"}</p>
        </div>

      </div>
    </div>
  );
}

export default UserDetailsCard;